// ============================================================================
// Brampton Cash & Carry — single source of truth for static content + config
// ----------------------------------------------------------------------------
// BLUEFIG_VENDOR_URL: the external BlueFig vendor login/profile portal.
// Change this ONE value to update every Login / Sign Up and account CTA.
// ============================================================================
export const BLUEFIG_VENDOR_URL = "https://bluefig.ca/v/brampton-cash-and-carry";

export const BRAND = {
  name: "Brampton Cash & Carry",
  short: "Brampton C&C",
  tagline: "Your Restaurant Supply Partner",
  altTagline: "Bulk Supply. Better Pricing. Reliable Delivery.",
  phone: "+1 587-716-7950",
  email: "orders@bramptoncashandcarry.com",
  whatsapp: "+1 587-716-7950",
  address: "2959 Parsons Rd NW, Unit 1, Edmonton, AB T6N 1A3",
  hours: "Mon–Fri 8:00am–6:00pm · Sat 9:00am–4:00pm",
};

export const IMG = {
  heroWarehouse: "https://images.unsplash.com/photo-1553413077-190dd305871c?auto=format&fit=crop&w=1920&q=80",
  warehouseShelves: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&w=1400&q=80",
  workers: "https://images.pexels.com/photos/4487383/pexels-photo-4487383.jpeg?auto=compress&cs=tinysrgb&w=1400",
  worker2: "https://images.pexels.com/photos/4483610/pexels-photo-4483610.jpeg?auto=compress&cs=tinysrgb&w=1400",
  boxes: "https://images.pexels.com/photos/4498136/pexels-photo-4498136.jpeg?auto=compress&cs=tinysrgb&w=1400",
  chef: "https://images.pexels.com/photos/17086289/pexels-photo-17086289.jpeg?auto=compress&cs=tinysrgb&w=1400",
  spices: "https://images.unsplash.com/photo-1506368249639-73a05d6f6488?auto=format&fit=crop&w=1400&q=80",
  spiceMarket: "https://images.unsplash.com/photo-1596040033229-a9821ebd058d?auto=format&fit=crop&w=1400&q=80",
  spiceMarket2: "https://images.unsplash.com/photo-1542838132-92c53300491e?auto=format&fit=crop&w=1400&q=80",
  rice: "https://images.unsplash.com/photo-1586201375761-83865001e31c?auto=format&fit=crop&w=1400&q=80",
  groceries: "https://images.pexels.com/photos/4198015/pexels-photo-4198015.jpeg?auto=compress&cs=tinysrgb&w=1400",
  delivery: "https://images.pexels.com/photos/4391470/pexels-photo-4391470.jpeg?auto=compress&cs=tinysrgb&w=1400",
  mealPrep: "https://images.unsplash.com/photo-1578916171728-46686eac8d58?auto=format&fit=crop&w=1400&q=80",
  cleaning: "https://images.pexels.com/photos/4386466/pexels-photo-4386466.jpeg?auto=compress&cs=tinysrgb&w=1400",
  logistics: "https://images.pexels.com/photos/6169668/pexels-photo-6169668.jpeg?auto=compress&cs=tinysrgb&w=1400",
};

export const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "About Us", to: "/about" },
  { label: "Product Categories", to: "/product-categories" },
  { label: "How It Works", to: "/how-it-works" },
  { label: "Delivery", to: "/delivery" },
  { label: "Online Ordering", to: "/online-ordering" },
  { label: "Reporting & Invoices", to: "/reporting" },
  { label: "Price Match", to: "/price-match" },
  { label: "Become a Customer", to: "/become-a-customer" },
  { label: "Contact Us", to: "/contact" },
];

export const TRUST_STATS = [
  { value: "500+", label: "Wholesale SKUs In Stock" },
  { value: "Free", label: "Delivery Across Edmonton" },
  { value: "Alberta", label: "Wide Delivery Available" },
  { value: "India & China", label: "Direct Import Network" },
];

export const BUSINESS_TYPES = [
  "Indian Restaurants", "Cafés", "Takeout Restaurants", "Caterers",
  "Bakeries", "Food Trucks", "Banquet Halls", "Grocery Stores",
  "Commercial Kitchens", "Hospitality Businesses",
];

export const CATEGORIES = [
  {
    slug: "rice-flour-grains",
    name: "Rice, Flour & Grains",
    tone: "bg-butter",
    items: ["Basmati rice", "Atta", "Maida", "Besan", "Lentils", "Beans", "Grains"],
    image: IMG.rice,
    desc: "Restaurant-size bags of basmati, flours, lentils and pulses milled for consistent commercial cooking.",
  },
  {
    slug: "spices-seasonings",
    name: "Spices & Seasonings",
    tone: "bg-peach",
    items: ["Whole spices", "Ground spices", "Masala blends", "Salt", "Seasoning mixes", "Bulk spice packs"],
    image: IMG.spices,
    desc: "Whole and ground spices, signature masala blends and bulk seasoning packs built for high-volume kitchens.",
  },
  {
    slug: "oils-cooking-essentials",
    name: "Oils & Cooking Essentials",
    tone: "bg-sage",
    items: ["Cooking oils", "Ghee", "Sauces", "Vinegars", "Cooking pastes", "Food colours"],
    image: IMG.groceries,
    desc: "Cooking oils, ghee, pastes, sauces and the everyday essentials your line cooks reach for all shift.",
  },
  {
    slug: "frozen-refrigerated",
    name: "Frozen & Refrigerated",
    tone: "bg-skyblue",
    items: ["Frozen vegetables", "Paneer", "Dairy products", "Frozen appetizers", "Restaurant-ready products"],
    image: IMG.mealPrep,
    desc: "Paneer, dairy, frozen vegetables and ready-to-fire appetizers kept cold from our dock to your kitchen.",
  },
  {
    slug: "canned-packaged",
    name: "Canned & Packaged Foods",
    tone: "bg-butter",
    items: ["Canned vegetables", "Tomato products", "Pickles", "Chutneys", "Sauces", "Bulk ingredients"],
    image: IMG.groceries,
    desc: "Tomato products, pickles, chutneys and shelf-stable bulk ingredients ordered by the case.",
  },
  {
    slug: "beverages-cafe",
    name: "Beverages & Café Supplies",
    tone: "bg-peach",
    items: ["Tea", "Coffee", "Soft drinks", "Juices", "Syrups", "Beverage ingredients"],
    image: IMG.spiceMarket2,
    desc: "Loose-leaf and bagged tea, coffee, syrups and beverage ingredients for cafés and dining rooms.",
  },
  {
    slug: "takeout-packaging",
    name: "Takeout & Packaging",
    tone: "bg-sage",
    items: ["Takeout containers", "Paper & plastic bags", "Aluminium trays", "Cups & lids", "Cutlery", "Napkins"],
    image: IMG.boxes,
    desc: "Containers, trays, cups, cutlery and tamper-evident packaging that keeps delivery orders travel-ready.",
  },
  {
    slug: "cleaning-sanitation",
    name: "Cleaning & Sanitation",
    tone: "bg-skyblue",
    items: ["Degreasers", "Dishwashing chemicals", "Garbage bags", "Gloves", "Sanitizers", "Paper towels"],
    image: IMG.cleaning,
    desc: "Degreasers, dish chemicals, sanitizers, gloves and consumables that keep your kitchen inspection-ready.",
  },
  {
    slug: "kitchen-essentials",
    name: "Kitchen Essentials",
    tone: "bg-butter",
    items: ["Foil", "Plastic wrap", "Parchment paper", "Storage containers", "Disposable gloves", "Small supplies"],
    image: IMG.mealPrep,
    desc: "Foil, wrap, parchment, storage and the small back-of-house supplies that always run out at the worst time.",
  },
];

export const IMPORTER_BENEFITS = [
  { title: "More Competitive Wholesale Pricing", desc: "Sourcing direct from trusted manufacturers removes unnecessary middlemen so your margins improve." },
  { title: "Consistent Product Availability", desc: "Planned container shipments and warehouse stock keep your regular items reliably available." },
  { title: "Products Selected for Restaurant Use", desc: "We curate case sizes, formats and specs specifically for busy commercial kitchens." },
];

export const ONBOARDING_STEPS = [
  { no: "01", title: "Create Your Wholesale Account", desc: "Enter your business information, delivery address and preferred delivery days. It takes just a few minutes." },
  { no: "02", title: "Get Approved & Receive Login Access", desc: "Our team reviews your account and unlocks wholesale pricing and the online ordering platform." },
  { no: "03", title: "Place Your Order", desc: "Choose products, quantities and delivery preferences. Orders can begin arriving as early as the next available delivery day." },
];

export const DELIVERY_AREAS = [
  { name: "Edmonton", note: "Free delivery on eligible orders", primary: true },
  { name: "St. Albert", note: "Scheduled delivery", primary: false },
  { name: "Sherwood Park", note: "Scheduled delivery", primary: false },
  { name: "Spruce Grove", note: "Scheduled delivery", primary: false },
  { name: "Stony Plain", note: "Scheduled delivery", primary: false },
  { name: "Leduc", note: "Scheduled delivery", primary: false },
  { name: "Beaumont", note: "Scheduled delivery", primary: false },
  { name: "Fort Saskatchewan", note: "Scheduled delivery", primary: false },
  { name: "Red Deer", note: "Route delivery", primary: false },
  { name: "Calgary", note: "Route delivery", primary: false },
];

export const WHY_CHOOSE = [
  "500+ Wholesale SKUs", "One Supplier for Multiple Categories", "Direct Imports",
  "Competitive Wholesale Pricing", "Price Match & Price Beat Opportunities", "Free Edmonton Delivery on Eligible Orders",
  "Alberta-Wide Delivery", "Flexible Delivery Scheduling", "Digital Ordering",
  "Organized Invoice Management", "Advanced Purchase Reporting", "Easy Reordering",
  "Dedicated Customer Support", "Fast Account Setup", "Restaurant-Focused Selection",
];

export const COMPARISON = {
  traditional: [
    "Phone calls and text messages",
    "Multiple suppliers to manage",
    "Missing or lost invoices",
    "Unclear order history",
    "Repetitive manual ordering",
    "Limited spending visibility",
  ],
  ecc: [
    "Online ordering, 24/7",
    "One-stop wholesale supply",
    "Centralized digital invoices",
    "Complete purchase history",
    "One-tap quick reorder",
    "Detailed purchasing reports",
  ],
};

export const TESTIMONIALS = [
  { quote: "Brampton Cash & Carry has made our weekly purchasing much easier. We reorder our regular supplies, access invoices in one place and work with a delivery schedule that fits our kitchen.", who: "Indian Restaurant Owner, Edmonton" },
  { quote: "One supplier for spices, packaging and cleaning products saves us hours every week. The pricing has been genuinely competitive against our old vendors.", who: "Café Operator, St. Albert" },
  { quote: "Deliveries arrive when they say they will, which matters when we're catering back-to-back events. The team is responsive and understands foodservice.", who: "Catering Business, Sherwood Park" },
  { quote: "Managing three locations used to mean three sets of invoices and phone orders. Now everything lives in one account with clean reporting.", who: "Multi-Location Restaurant Group" },
  { quote: "The reorder tool is the feature we didn't know we needed. Routine orders that took twenty minutes now take two.", who: "Commercial Kitchen Manager, Leduc" },
];

export const FAQS = [
  { q: "Who can open a wholesale account?", a: "Wholesale accounts are available to registered food businesses — restaurants, cafés, caterers, bakeries, food trucks, grocery stores, banquet halls and commercial kitchens across Edmonton and Alberta." },
  { q: "Do you sell to the public?", a: "We are a wholesale foodservice supplier focused on food businesses. Retail and public policies can be confirmed by our team; our pricing and platform are built for approved commercial accounts." },
  { q: "Is delivery free in Edmonton?", a: "We offer free delivery across Edmonton on qualifying wholesale orders. Minimum order values and eligibility are confirmed when your account is set up." },
  { q: "Do you deliver outside Edmonton?", a: "Yes. We deliver across Alberta including St. Albert, Sherwood Park, Spruce Grove, Leduc, Red Deer, Calgary and more. Timing, fees and minimums may vary by location." },
  { q: "Is there a minimum order?", a: "Minimum order values may apply, particularly for free delivery and out-of-town routes. Our team confirms the details for your business during onboarding." },
  { q: "How quickly can a new customer begin ordering?", a: "Once your account is approved, ordering can begin quickly — with new approved customers often receiving their first order as early as the next available delivery day." },
  { q: "Can I request specific delivery days?", a: "Absolutely. We build recurring delivery schedules around your operation and can adjust as your needs change." },
  { q: "Can I upload my current supplier invoice?", a: "Yes. Share a recent invoice or quote through our Price Match request and we will review the products and pricing to match or beat eligible items where possible." },
  { q: "How does the price-match program work?", a: "Send us a recent supplier invoice or quotation. We review the products, quantities and specifications and work to match or beat the eligible price, subject to availability and approval." },
  { q: "Can you source products that are not currently listed?", a: "Often, yes. We regularly source additional products for regular customers through our import and supplier network — just ask." },
  { q: "How do I access my invoices?", a: "Your account dashboard on the ordering platform stores current and past invoices, credit notes and downloadable PDFs, organized in one secure place." },
  { q: "Can I reorder a previous order?", a: "Yes. You can copy a previous order, adjust quantities, remove unavailable items, add new products and choose a delivery date in minutes." },
  { q: "Can I download purchasing reports?", a: "Yes. The reporting dashboard provides monthly summaries, spending by category, most-ordered items and exportable reports with date filters." },
  { q: "Do you offer customer-specific pricing?", a: "Pricing can be tailored to your volume, case quantities, product mix, delivery frequency and overall relationship. Request wholesale pricing to get started." },
  { q: "Do you provide credit accounts?", a: "Credit terms may be available to qualifying businesses. Indicate your interest on the account application and our team will follow up." },
  { q: "What areas of Alberta do you serve?", a: "We primarily serve Edmonton and the surrounding region, with scheduled and route delivery available to communities across Alberta." },
];

export const DASHBOARD = {
  spendTrend: [
    { m: "Apr", v: 6800 }, { m: "May", v: 7400 }, { m: "Jun", v: 7100 },
    { m: "Jul", v: 8200 }, { m: "Aug", v: 9100 }, { m: "Sep", v: 8700 },
  ],
  byCategory: [
    { name: "Spices", value: 32 }, { name: "Rice & Flour", value: 26 },
    { name: "Packaging", value: 18 }, { name: "Oils", value: 14 }, { name: "Cleaning", value: 10 },
  ],
  topItems: [
    { name: "Basmati Rice 20kg", qty: 48 },
    { name: "Takeout Containers (500)", qty: 36 },
    { name: "Garam Masala 5kg", qty: 30 },
    { name: "Canola Oil 16L", qty: 24 },
  ],
};
